import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  AvatarButton,
  AvatarSettings,
} from '@frameio/shared/components/AvatarEditor/components';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import {
  AlertInline,
  Text,
  TextField,
  theme,
  useHoverIntent,
  whenFocused,
  whenHovered,
} from '@frameio/vapor';
import { IconEdit, IconLock } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectAvatarEditor';
import { ProjectAvatarImageIcon } from './ProjectAvatarImageIcon';

const INPUT_CHAR_LIMIT = 255;
const AVATAR_IMAGE_LENGTH = 212;
const FOCUS_TIMEOUT_DURATION = 50;
TEXT.DEFINE({
  /** Label for the avatar editor action */
  editAvatar: 'Edit project avatar',
  /** Label for the edit avatar icon */
  editAvatarIconLabel: 'Edit Image',
  /** Fallback project name when none is set */
  defaultProjectName: 'Untitled Project',
  /** Placeholder text for the project name input */
  addProjectName: 'Add project name',
  /** Accessible label for the project name input */
  projectNameLabel: 'Project name',
  /** Message shown when the avatar image fails to load */
  avatarLoadError: 'Failed to load. Try again.',
  /** Accessible label for the lock icon shown when a project is restricted */
  restrictedLabel: 'Project is restricted',
});
type ProjectAvatarEditorProps = {
  avatarThumb?: string | null;
  avatarOriginal?: string | null;
  name: string;
  onNameChange: (name: string) => void;
  onNameKeyDown?: (evt: KeyboardEvent) => void;
  onSave: (blob: Blob) => void;
  isDeleteAvatar?: boolean;
  isRestricted?: boolean | null;
  nameInputDataTestId?: string;
  isWorkfront?: boolean;
  shouldFocusOnMount?: boolean;
};
function renderProjectAvatar({
  hasValidThumb,
  hasImageError,
  isWebClient,
}: {
  hasValidThumb: boolean;
  hasImageError: boolean;
  isWebClient: boolean;
}) {
  if (!isWebClient) {
    return null;
  }
  if (hasImageError) {
    return (
      <>
        <ErrorBanner variant="error">{TEXT.avatarLoadError}</ErrorBanner>
        <UploadIconContainer>
          <ProjectAvatarImageIcon />
        </UploadIconContainer>
      </>
    );
  }
  if (hasValidThumb) {
    return (
      <EditIconContainer>
        <IconEdit
          color={theme.colors.components.button.secondary.icon}
          size={16}
        />
        <Text variant="link">{TEXT.editAvatarIconLabel}</Text>
      </EditIconContainer>
    );
  }
  return (
    <UploadIconContainer>
      <ProjectAvatarImageIcon />
    </UploadIconContainer>
  );
}
export const ProjectAvatarEditor = ({
  avatarOriginal,
  avatarThumb,
  name,
  onNameChange,
  onNameKeyDown,
  onSave,
  isDeleteAvatar = false,
  nameInputDataTestId = 'workspaces-create-project-modal--name-field',
  isRestricted,
  isWorkfront = false,
  shouldFocusOnMount = false,
}: ProjectAvatarEditorProps) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isWebClient = useIsMatchingClientName('web');
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [avatarThumb]);

  const hasValidThumb =
    Boolean(avatarThumb) && !isDeleteAvatar && !hasImageError;
  const { bind: hoverBind } = useHoverIntent(() => {
    if (avatarOriginal === null) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = avatarOriginal || '';
  });
  useEffect(() => {
    if (shouldFocusOnMount && nameInputRef.current) {
      const timeoutId = setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, FOCUS_TIMEOUT_DURATION);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [shouldFocusOnMount]);
  return (
    <StyledContainer>
      <AvatarSettings
        avatarSrc={avatarOriginal}
        avatarThumb={avatarThumb}
        editLabel={TEXT.editAvatar}
        onSave={onSave}
        shape="square"
      >
        {({ isEditorOpen, nextAvatarSrc, onClick, ...bind }) => (
          <>
            <StyledAvatarButton
              avatar={
                hasValidThumb ? (
                  <>
                    <AvatarImage
                      alt="project-avatar-image"
                      aria-hidden={true}
                      onError={() => setHasImageError(true)}
                      src={avatarThumb ?? ''}
                    />
                    <Overlay />
                  </>
                ) : (
                  <BlankTemplateAvatarImage />
                )
              }
              avatarSize={160}
              data-testid="fio-avatar-editor-btn"
              delayMount={0}
              disabled={!isWebClient}
              disableOverlay={true}
              label={`${TEXT.editAvatarIconLabel} - ${TEXT.editAvatar}`}
              nextAvatarSrc={nextAvatarSrc || ''}
              onClick={onClick}
              shape="square"
              showOverlay={isEditorOpen}
              style={{
                width: '100%',
                height: '100%',
              }}
              {...bind}
              {...hoverBind}
            />

            {renderProjectAvatar({
              hasValidThumb,
              hasImageError,
              isWebClient,
            })}

            <ProjectName>
              <StyledTextField
                ref={nameInputRef}
                autoFocus={shouldFocusOnMount}
                color="primary"
                data-testid={nameInputDataTestId}
                defaultValue={TEXT.defaultProjectName}
                disabled={isWorkfront}
                errorVariant="inline"
                hideLabel={true}
                label={TEXT.projectNameLabel}
                maxLength={INPUT_CHAR_LIMIT}
                onChange={(e) => onNameChange(e.currentTarget.value)}
                onClick={(e) => {
                  if (isWorkfront) {
                    e.stopPropagation();
                    e.preventDefault();
                  } else {
                    nameInputRef.current?.focus();
                  }
                }}
                onKeyDown={onNameKeyDown}
                value={name}
              />
            </ProjectName>

            {isRestricted && (
              <RestrictedIconContainer>
                <IconLock
                  aria-label={TEXT.restrictedLabel}
                  role="img"
                  size={16}
                />
              </RestrictedIconContainer>
            )}
          </>
        )}
      </AvatarSettings>
    </StyledContainer>
  );
};

const SharedIconContainerStyles = css`
  align-items: center;
  background-color: ${theme.colors.components.button.neutral.background};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
`;

const EditIconContainer = styled.div`
  ${SharedIconContainerStyles}
  border-radius: ${theme.radii[6]};
  gap: ${theme.spaces[4]};
  padding: ${theme.spaces[2]} ${theme.spaces[8]};
`;

const UploadIconContainer = styled.div`
  ${SharedIconContainerStyles}
  border-radius: ${theme.radii[8]};
  padding: ${theme.spaces[8]};
`;

const ErrorBanner = styled(AlertInline)`
  border-radius: ${theme.radii[4]};
  left: ${theme.spaces[4]};
  pointer-events: none;
  position: absolute;
  right: ${theme.spaces[4]};
  top: ${theme.spaces[4]};
`;

const StyledAvatarButton = styled(AvatarButton)`
  ${whenFocused} {
    /* Style any EditIconContainer that follows this button */
    ~ ${EditIconContainer}, ~ ${UploadIconContainer} {
      background-color: ${theme.colors.components.button.neutral.interactive
        .background.hover};
      outline: 1.5px solid ${theme.colors.border.interactive.focused};
    }
  }
`;

const StyledContainer = styled.div`
  background-color: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.radii[8]};
  box-shadow: ${theme.shadows['2dp']};
  padding: ${theme.spaces[4]} ${theme.spaces[4]} ${theme.spaces[32]};
  transform: scale(1);
  transition:
    opacity 0.1s ${theme.transitions.eases.easeOut},
    transform 0.1s ${theme.transitions.eases.easeOut};

  ${whenHovered} {
    ${EditIconContainer}, ${UploadIconContainer} {
      background-color: ${theme.colors.components.button.neutral.interactive
        .background.hover};
    }
  }
`;

const AvatarImage = styled.img`
  background-clip: padding-box;
  border-radius: ${theme.radii[4]};
  height: ${AVATAR_IMAGE_LENGTH}px;
  inset: 0;
  object-fit: cover;
  position: relative;
  width: ${AVATAR_IMAGE_LENGTH}px;
`;

const Overlay = styled.div`
  align-items: center;
  background-color: ${theme.colors.overlay.low};
  border-radius: ${theme.radii[4]};
  display: flex;
  height: ${AVATAR_IMAGE_LENGTH}px;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: ${AVATAR_IMAGE_LENGTH}px;
`;

const ProjectName = styled.div`
  bottom: 40px;
  left: ${theme.spaces[12]};
  overflow: hidden;
  padding: ${theme.spaces[4]};
  position: absolute;
  right: ${theme.spaces[12]};
`;

const BlankTemplateAvatarImage = styled.div`
  background-color: ${theme.colors.background.panel};
  border-radius: ${theme.radii[4]};
  height: ${AVATAR_IMAGE_LENGTH}px;
  width: ${AVATAR_IMAGE_LENGTH}px;
`;

const RestrictedIconContainer = styled.div`
  background-color: ${theme.colors.components.button.neutral.background};
  border: 0.5px solid ${theme.colors.border.tertiaryStark};
  border-radius: ${theme.radii[4]};
  display: flex;
  padding: ${theme.spaces[2]};
  position: absolute;
  right: ${theme.spaces[8]};
  top: ${theme.spaces[8]};
`;

const StyledTextField = styled(TextField)`
  input {
    font-family: ${theme.fonts.body};
    font-size: ${theme.spaces[16]};
    font-weight: ${theme.fontWeights.medium};
    padding: ${theme.spaces[4]};
  }
`;
